import { useCallback, useEffect, useState } from 'react';
import { Activity, Award, ChevronRight, Medal, Target, TrendingUp } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, EmptyState, ErrorState, LoadingState, SectionHeading } from '../components/ui';
import { loadAllMyResults, type ResultRow } from '../lib/studentUtilities';
import { loadTestLeaderboards, type LeaderboardEntry } from '../lib/studentTestApi';
import { countClass, pointer, pointerId, queryClass, type ParseRecord } from '../lib/parse';

type CourseTest = ParseRecord & { TestTitle?: string; CourseTitle?: string; Type?: string; IsPublished?: boolean; IsDeleted?: boolean };
type CourseTestRow = { test: CourseTest; latest?: ResultRow; attempts: number; standing?: LeaderboardEntry; totalLearners: number; leaderboard: LeaderboardEntry[] };
const PAGE_SIZE = 25;

export function StudentCoursePerformance() {
  const { courseId = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseTitle, setCourseTitle] = useState('Course performance');
  const [tests, setTests] = useState<CourseTestRow[]>([]);
  const [page, setPage] = useState(0);
  const [totalTests, setTotalTests] = useState(0);
  const [summary, setSummary] = useState({ average: 0, attemptedTests: 0, attempts: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [courseRows, courseTests, myResults] = await Promise.all([
        queryClass('elearning_Course', { limit: 1, keys: ['CourseTitle', 'Performance'], where: (query) => query.equalTo('objectId', courseId) }),
        queryClass('test_TestInfo', {
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
          order: 'TestTitle',
          keys: ['TestTitle', 'CourseTitle', 'CoursePtr', 'Type', 'IsPublished', 'IsDeleted'],
          where: (query) => {
            query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
            query.notEqualTo('IsDeleted', true);
            query.notEqualTo('IsPublished', false);
            query.notEqualTo('Type', 'Folder');
          },
        }) as Promise<CourseTest[]>,
        loadAllMyResults(),
      ]);
      const course = courseRows[0];
      if (!course) throw new Error('This course could not be found.');
      setCourseTitle(String(course.CourseTitle ?? 'Course performance'));

      const resultTestIds = [...new Set(myResults.map((row) => pointerId(row.TestInfoPtr)).filter((id): id is string => Boolean(id)))];
      const attemptedTests = (await Promise.all(Array.from({ length: Math.ceil(resultTestIds.length / 100) }, (_, index) => {
        const ids = resultTestIds.slice(index * 100, (index + 1) * 100);
        return queryClass('test_TestInfo', {
          limit: ids.length,
          keys: ['TestTitle', 'CourseTitle', 'CoursePtr', 'Type', 'IsPublished', 'IsDeleted'],
          where: (query) => {
            query.containedIn('objectId', ids);
            query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
            query.notEqualTo('IsDeleted', true);
            query.notEqualTo('Type', 'Folder');
          },
        }) as Promise<CourseTest[]>;
      }))).flat();
      const attemptedTestIds = new Set(attemptedTests.map((test) => test.objectId));
      const courseResults = myResults.filter((row) => {
        const testId = pointerId(row.TestInfoPtr);
        return (testId && attemptedTestIds.has(testId)) || (!testId && row.CourseTitle === course.CourseTitle);
      });
      const resultsByTest = new Map<string, ResultRow[]>();
      for (const result of courseResults) {
        const id = pointerId(result.TestInfoPtr);
        if (id) {
          const rows = resultsByTest.get(id) ?? [];
          rows.push(result);
          resultsByTest.set(id, rows);
        }
      }
      for (const rows of resultsByTest.values()) rows.sort((left, right) => String(right.TestDate ?? right.createdAt ?? '').localeCompare(String(left.TestDate ?? left.createdAt ?? '')));
      const latestResults = [...resultsByTest.values()].map((rows) => rows[0]);
      setSummary({
        average: latestResults.length ? Math.round(latestResults.reduce((sum, row) => sum + Number(row.Percentage ?? 0), 0) / latestResults.length) : 0,
        attemptedTests: latestResults.length,
        attempts: [...resultsByTest.values()].reduce((sum, rows) => sum + rows.length, 0),
      });

      const publishedCount = await countClass('test_TestInfo', {
        where: (query) => {
          query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
          query.notEqualTo('IsDeleted', true);
          query.notEqualTo('IsPublished', false);
          query.notEqualTo('Type', 'Folder');
        },
      });
      const retiredTests = attemptedTests.filter((test) => test.IsPublished === false);
      const total = publishedCount + retiredTests.length;
      setTotalTests(total);
      const visibleTests = page === 0 ? [...courseTests, ...retiredTests] : courseTests;
      const visibleTestIds = new Set(visibleTests.map((test) => test.objectId));
      const testedIds = [...resultsByTest.keys()].filter((id) => visibleTestIds.has(id));
      const rankings = await loadTestLeaderboards(testedIds);
      const rows = visibleTests.map((test) => {
        const attempts = resultsByTest.get(test.objectId) ?? [];
        const leaderboard = rankings.get(test.objectId) ?? [];
        return { test, latest: attempts[0], attempts: attempts.length, standing: leaderboard.find((entry) => entry.isMine), totalLearners: leaderboard.length, leaderboard };
      });
      rows.sort((left, right) => {
        const leftDate = String(left.latest?.TestDate ?? left.latest?.createdAt ?? '');
        const rightDate = String(right.latest?.TestDate ?? right.latest?.createdAt ?? '');
        if (leftDate && rightDate) return rightDate.localeCompare(leftDate);
        if (leftDate) return -1;
        if (rightDate) return 1;
        return String(left.test.TestTitle ?? '').localeCompare(String(right.test.TestTitle ?? ''));
      });
      setTests(rows);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Course performance could not load.');
    } finally { setLoading(false); }
  }, [courseId, page]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <LoadingState />;
  if (error) return <div className="utility-page"><ErrorState onRetry={() => void load()} /></div>;
  const pageCount = Math.max(1, Math.ceil(totalTests / PAGE_SIZE));

  return <div className="utility-page course-performance-page">
    <Link to={`/courses/${courseId}`} className="back-link">← {courseTitle}</Link>
    <section className="utility-hero"><div className="utility-icon"><Activity size={28} /></div><div><h1>Course performance</h1><p>Your results and standing for each test in {courseTitle}.</p></div></section>
    <section className="test-stats">
      <div><TrendingUp size={18} /><strong>{summary.average}%</strong><span>Latest score average</span></div>
      <div><Award size={18} /><strong>{summary.attemptedTests}</strong><span>Tests attempted</span></div>
      <div><Target size={18} /><strong>{summary.attempts}</strong><span>Scored attempts</span></div>
    </section>
    <section className="detail-section">
      <SectionHeading title="Test results and standing" />
      {tests.length ? <div className="course-performance-list">{tests.map(({ test, latest, attempts, standing, totalLearners, leaderboard }) => <article className="course-performance-row" key={test.objectId}>
        <div className="course-performance-main">
          <span className="course-performance-icon"><Medal size={18} /></span>
          <div><strong>{test.TestTitle ?? 'Untitled test'}</strong><small>{attempts ? `${attempts} attempt${attempts === 1 ? '' : 's'} · Latest ${latest?.TestDate ? new Date(latest.TestDate).toLocaleDateString() : 'date unavailable'}` : 'Not attempted yet'}</small></div>
          {latest && <Badge tone={Number(latest.Percentage ?? 0) >= 60 ? 'green' : Number(latest.Percentage ?? 0) >= 40 ? 'teal' : 'red'}>{Number(latest.Percentage ?? 0)}%</Badge>}
        </div>
        {standing && <p className="course-performance-standing"><Medal size={15} /> Best standing: <strong>#{standing.rank}</strong> of {totalLearners} learners</p>}
        {attempts > 0 && <details className="course-performance-leaderboard">
          <summary>View leaderboard <ChevronRight size={14} /></summary>
          <ol className="leaderboard-list" aria-label={`${test.TestTitle ?? 'Test'} leaderboard`}>
            {leaderboard.slice(0, 5).map((entry) => <li className={entry.isMine ? 'mine' : ''} key={entry.objectId}><span className={`leaderboard-rank${entry.rank <= 3 ? ` top-${entry.rank}` : ''}`} aria-label={`Rank ${entry.rank}`}>#{entry.rank}</span><strong>{entry.isMine ? 'You' : entry.learnerName}</strong><span className="leaderboard-score">{entry.percentage}%</span></li>)}
            {standing && standing.rank > 5 && <li className="mine"><span className="leaderboard-rank" aria-label={`Rank ${standing.rank}`}>#{standing.rank}</span><strong>You</strong><span className="leaderboard-score">{standing.percentage}%</span></li>}
          </ol>
        </details>}
        <div className="course-performance-actions">
          {latest ? <Link to={`/tests/result/${latest.objectId}`}><Button variant="soft">View result</Button></Link> : test.IsPublished !== false ? <Link to={`/tests/${test.objectId}`}><Button variant="soft">Open test</Button></Link> : <span>Past test</span>}
        </div>
      </article>)}</div> : <EmptyState icon={Target} title="No tests in this course" description="Published tests for this course will appear here." />}
      {totalTests > PAGE_SIZE && <nav className="course-performance-pagination" aria-label="Test results pages">
        <Button variant="soft" disabled={loading || page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>Previous</Button>
        <span>Page {Math.min(page + 1, pageCount)} of {pageCount}</span>
        <Button variant="soft" disabled={loading || page + 1 >= pageCount} onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}>Next</Button>
      </nav>}
    </section>
  </div>;
}
